import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
  Receipt,
  Users,
  Menu,
  X,
  ChevronLeft,
  ArrowLeft,
  Briefcase,
  BarChart3,
  Bell,
  Settings,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../../AppContext";
import AccountantContractorEmployees from "./AccountantContractorEmployees";
import AccountantContractorExpenses from "./AccountantContractorExpenses";
import axios from "axios";
import { MyDomain } from "../../../App";

const COLORS = {
  primary: "#0ea5e9",
  primaryHover: "#0284c7",
  success: "#10B981",
  danger: "#EF4444",
  textPrimary: "#111827",
  textSecondary: "#6B7280",
  bgPrimary: "#ffffff",
  bgSecondary: "#F9FAFB",
  bgTertiary: "#F3F4F6",
  border: "#e5e7eb",
};

export default function AccountantHomeContractorMainPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { contractorId, contractorName, contractorData } = location.state || {};
  const { Lang, AllData, setAllData } = useAppContext();
  const isRTL = Lang === "ar" || Lang === "he";

  const [activeTab, setActiveTab] = useState("expenses");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchContractorData();
  }, [contractorId]);

  const fetchContractorData = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${MyDomain}Employee/GetAllData`, {
        UserId: contractorId,
      });

      console.log("✅ Contractor data loaded:", response.data);
      setAllData(response.data);
    } catch (error) {
      console.error("❌ Error fetching contractor data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    history.goBack();
  };

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div className={`loading-wrapper ${isRTL ? "rtl" : ""}`}>
          <div className="loading-spinner"></div>
          <p className="loading-text">{t("loading")}</p>
        </div>
      </>
    );
  }

  const menuItems = [
    {
      id: "expenses",
      label: t("expenses"),
      icon: Receipt,
      color: "#0ea5e9",
      component: AccountantContractorExpenses,
    },
    {
      id: "employees",
      label: t("employees"),
      icon: Users,
      color: "#8b5cf6",
      component: AccountantContractorEmployees,
    },
  ];

  const ActiveComponent = menuItems.find(
    (item) => item.id === activeTab,
  )?.component;

  return (
    <>
      <style>{styles}</style>
      <div className={`contractor-main-container ${isRTL ? "rtl" : ""}`}>
        {/* Desktop Sidebar */}
        <aside
          className={`desktop-sidebar ${sidebarOpen ? "open" : "collapsed"} ${isRTL ? "rtl" : ""}`}
        >
          {/* Sidebar Header */}
          <div className={`sidebar-header ${isRTL ? "rtl" : ""}`}>
            {sidebarOpen ? (
              <>
                <div className={`sidebar-logo ${isRTL ? "rtl" : ""}`}>
                  <div className="logo-icon-wrapper">
                    <Briefcase size={22} color={COLORS.primary} />
                  </div>
                  <span className="logo-text">{t("contractor")}</span>
                </div>
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="sidebar-toggle-btn"
                >
                  <ChevronLeft
                    size={20}
                    className={`chevron-icon ${isRTL ? "rtl" : ""} ${sidebarOpen ? "open" : ""}`}
                  />
                </button>
              </>
            ) : (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="sidebar-toggle-btn-collapsed"
              >
                <Briefcase size={24} color={COLORS.primary} />
              </button>
            )}
          </div>

          {/* Contractor Info Card */}
          {sidebarOpen ? (
            <div className={`contractor-info-card ${isRTL ? "rtl" : ""}`}>
              <div className={`contractor-info-content ${isRTL ? "rtl" : ""}`}>
                <div className="contractor-avatar">
                  <span className="contractor-avatar-text">
                    {contractorName?.charAt(0)?.toUpperCase() || "C"}
                  </span>
                </div>
                <div className={`contractor-details ${isRTL ? "rtl" : ""}`}>
                  <p className={`contractor-name ${isRTL ? "rtl" : ""}`}>
                    {contractorName || t("contractor")}
                  </p>
                  <p className={`contractor-role ${isRTL ? "rtl" : ""}`}>
                    {t("contractorAccount")}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="contractor-info-collapsed">
              <div className="contractor-avatar">
                <span className="contractor-avatar-text">
                  {contractorName?.charAt(0)?.toUpperCase() || "C"}
                </span>
              </div>
            </div>
          )}

          {/* Navigation Menu */}
          <nav className="sidebar-nav">
            <div className="nav-items">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;

                return (
                  <div key={item.id} className="nav-item-wrapper">
                    <button
                      onClick={() => setActiveTab(item.id)}
                      className={`nav-item ${isActive ? "active" : ""} ${isRTL ? "rtl" : ""} ${!sidebarOpen ? "collapsed" : ""}`}
                      style={{
                        backgroundColor: isActive ? "#EFF6FF" : "transparent",
                      }}
                    >
                      <Icon
                        size={22}
                        color={isActive ? item.color : COLORS.textSecondary}
                        className="nav-icon"
                      />
                      {sidebarOpen && (
                        <span
                          className="nav-label"
                          style={{
                            color: isActive ? item.color : COLORS.textPrimary,
                            fontWeight: isActive ? 600 : 500,
                          }}
                        >
                          {item.label}
                        </span>
                      )}

                      {/* Tooltip for collapsed sidebar */}
                      {!sidebarOpen && (
                        <div className={`nav-tooltip ${isRTL ? "rtl" : ""}`}>
                          {item.label}
                          <div
                            className={`tooltip-arrow ${isRTL ? "rtl" : ""}`}
                          ></div>
                        </div>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </nav>

          {/* Back Button */}
          <div className="sidebar-footer">
            <button
              onClick={handleGoBack}
              className={`back-button ${isRTL ? "rtl" : ""} ${!sidebarOpen ? "collapsed" : ""}`}
            >
              <ArrowLeft
                size={22}
                color={COLORS.danger}
                className={`back-icon ${isRTL ? "rtl" : ""}`}
              />
              {sidebarOpen && <span className="back-label">{t("back")}</span>}
            </button>
          </div>
        </aside>

        {/* Mobile Overlay */}
        {mobileMenuOpen && (
          <div
            className="mobile-overlay"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Mobile Sidebar */}
        <aside
          className={`mobile-sidebar ${mobileMenuOpen ? "open" : ""} ${isRTL ? "rtl" : ""}`}
        >
          {/* Mobile Header */}
          <div className={`mobile-sidebar-header ${isRTL ? "rtl" : ""}`}>
            <div className={`sidebar-logo ${isRTL ? "rtl" : ""}`}>
              <div className="logo-icon-wrapper">
                <Briefcase size={22} color={COLORS.primary} />
              </div>
              <span className="logo-text">{t("contractor")}</span>
            </div>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="mobile-close-btn"
            >
              <X size={20} color={COLORS.textSecondary} />
            </button>
          </div>

          {/* Mobile Contractor Info */}
          <div className={`contractor-info-card ${isRTL ? "rtl" : ""}`}>
            <div className={`contractor-info-content ${isRTL ? "rtl" : ""}`}>
              <div className="contractor-avatar">
                <span className="contractor-avatar-text">
                  {contractorName?.charAt(0)?.toUpperCase() || "C"}
                </span>
              </div>
              <div className={`contractor-details ${isRTL ? "rtl" : ""}`}>
                <p className={`contractor-name ${isRTL ? "rtl" : ""}`}>
                  {contractorName || t("contractor")}
                </p>
                <p className={`contractor-role ${isRTL ? "rtl" : ""}`}>
                  {t("contractorAccount")}
                </p>
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          <nav className="sidebar-nav mobile">
            <div className="nav-items">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;

                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveTab(item.id);
                      setMobileMenuOpen(false);
                    }}
                    className={`nav-item ${isActive ? "active" : ""} ${isRTL ? "rtl" : ""}`}
                    style={{
                      backgroundColor: isActive ? "#EFF6FF" : "transparent",
                    }}
                  >
                    <Icon
                      size={22}
                      color={isActive ? item.color : COLORS.textSecondary}
                      className="nav-icon"
                    />
                    <span
                      className="nav-label"
                      style={{
                        color: isActive ? item.color : COLORS.textPrimary,
                        fontWeight: isActive ? 600 : 500,
                      }}
                    >
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Mobile Back Button */}
          <div className="sidebar-footer">
            <button
              onClick={handleGoBack}
              className={`back-button ${isRTL ? "rtl" : ""}`}
            >
              <ArrowLeft
                size={22}
                color={COLORS.danger}
                className={`back-icon ${isRTL ? "rtl" : ""}`}
              />
              <span className="back-label">{t("back")}</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <div className="main-content">
          {/* Top Header */}
          <header className={`main-header ${isRTL ? "rtl" : ""}`}>
            <div className={`header-left ${isRTL ? "rtl" : ""}`}>
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="mobile-menu-btn"
              >
                <Menu size={24} color={COLORS.textSecondary} />
              </button>
              <h1 className={`page-title ${isRTL ? "rtl" : ""}`}>
                {menuItems.find((item) => item.id === activeTab)?.label ||
                  t("dashboard")}
              </h1>
            </div>

            {/* Header Actions */}
            <div className={`header-actions ${isRTL ? "rtl" : ""}`}>
              {/* Notifications */}
              <button className="header-action-btn notification-btn">
                <Bell size={20} color={COLORS.textSecondary} />
                <span className="notification-dot"></span>
              </button>

              {/* Stats */}
              <button className="header-action-btn">
                <BarChart3 size={20} color={COLORS.textSecondary} />
              </button>

              {/* Settings */}
              <button className="header-action-btn mobile-hidden">
                <Settings size={20} color={COLORS.textSecondary} />
              </button>

              {/* Mobile Avatar */}
              <div className="mobile-avatar">
                <span className="mobile-avatar-text">
                  {contractorName?.charAt(0)?.toUpperCase() || "C"}
                </span>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="page-content">
            {ActiveComponent && (
              <ActiveComponent
                contractorId={contractorId}
                contractorName={contractorName}
                contractorData={contractorData}
              />
            )}
          </main>
        </div>
      </div>
    </>
  );
}

const styles = `
/* =============== BASE STYLES =============== */
.contractor-main-container {
  display: flex;
  height: 100vh;
  background-color: #F9FAFB;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.contractor-main-container.rtl {
  direction: rtl;
}

/* =============== LOADING STATE =============== */
.loading-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #F9FAFB;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #0ea5e9;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  margin-top: 16px;
  font-size: 16px;
  color: #6B7280;
  text-align: center;
  font-weight: 500;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* =============== DESKTOP SIDEBAR =============== */
.desktop-sidebar {
  display: none;
  flex-direction: column;
  background-color: #ffffff;
  width: 280px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border-right: 1px solid #e5e7eb;
}

.desktop-sidebar.rtl {
  border-right: none;
  border-left: 1px solid #e5e7eb;
}

.desktop-sidebar.collapsed {
  width: 80px;
}

@media (min-width: 1024px) {
  .desktop-sidebar {
    display: flex;
  }
}

/* =============== SIDEBAR HEADER =============== */
.sidebar-header {
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #ffffff;
}

.sidebar-header.rtl {
  flex-direction: row-reverse;
}

.sidebar-logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sidebar-logo.rtl {
  flex-direction: row-reverse;
}

.logo-icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(14, 165, 233, 0.2));
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}

.logo-text {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.3px;
}

.sidebar-toggle-btn,
.sidebar-toggle-btn-collapsed {
  padding: 8px;
  border-radius: 8px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-toggle-btn:hover,
.sidebar-toggle-btn-collapsed:hover {
  background-color: #F3F4F6;
}

.chevron-icon {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #6B7280;
}

.chevron-icon.open {
  transform: rotate(180deg);
}

.chevron-icon.rtl.open {
  transform: rotate(0deg);
}

.chevron-icon.rtl {
  transform: rotate(180deg);
}

.sidebar-toggle-btn-collapsed {
  width: 100%;
}

/* =============== CONTRACTOR INFO =============== */
.contractor-info-card {
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
}

.contractor-info-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.contractor-info-content.rtl {
  flex-direction: row-reverse;
}

.contractor-avatar {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.2));
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.contractor-avatar-text {
  font-size: 20px;
  font-weight: 700;
  color: #10B981;
}

.contractor-details {
  flex: 1;
  min-width: 0;
}

.contractor-details.rtl {
  text-align: right;
}

.contractor-name {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 2px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contractor-name.rtl {
  text-align: right;
}

.contractor-role {
  font-size: 12px;
  color: #6B7280;
  margin: 0;
}

.contractor-role.rtl {
  text-align: right;
}

.contractor-info-collapsed {
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: center;
  background-color: #ffffff;
}

/* =============== NAVIGATION =============== */
.sidebar-nav {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  background-color: #ffffff;
}

.nav-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.nav-item-wrapper {
  position: relative;
}

.nav-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.nav-item.rtl {
  flex-direction: row-reverse;
}

.nav-item.collapsed {
  justify-content: center;
  gap: 0;
}

.nav-item:hover {
  background-color: #F3F4F6 !important;
  transform: translateX(4px);
}

.nav-item.rtl:hover {
  transform: translateX(-4px);
}

.nav-item.active {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.nav-item.active:hover {
  transform: translateX(0);
}

.nav-icon {
  flex-shrink: 0;
}

.nav-label {
  font-size: 14px;
  flex: 1;
  text-align: left;
}

.nav-item.rtl .nav-label {
  text-align: right;
}

/* Tooltip */
.nav-tooltip {
  position: absolute;
  left: 100%;
  margin-left: 8px;
  top: 50%;
  transform: translateY(-50%);
  padding: 8px 12px;
  background-color: #1f2937;
  color: #ffffff;
  font-size: 13px;
  border-radius: 8px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  z-index: 50;
  pointer-events: none;
  font-weight: 500;
}

.nav-tooltip.rtl {
  left: auto;
  right: 100%;
  margin-left: 0;
  margin-right: 8px;
}

.nav-item-wrapper:hover .nav-tooltip {
  opacity: 1 !important;
  visibility: visible !important;
}

.tooltip-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 100%;
  border-width: 4px;
  border-style: solid;
  border-color: transparent;
  border-right-color: #1f2937;
}

.tooltip-arrow.rtl {
  right: auto;
  left: 100%;
  border-right-color: transparent;
  border-left-color: #1f2937;
}

/* =============== SIDEBAR FOOTER =============== */
.sidebar-footer {
  padding: 16px;
  border-top: 1px solid #e5e7eb;
  background-color: #ffffff;
}

.back-button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  border: none;
  background-color: transparent;
  color: #EF4444;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  font-weight: 600;
}

.back-button.rtl {
  flex-direction: row-reverse;
}

.back-button.collapsed {
  justify-content: center;
  gap: 0;
}

.back-button:hover {
  background-color: #fef2f2;
  transform: translateX(4px);
}

.back-button.rtl:hover {
  transform: translateX(-4px);
}

.back-icon {
  flex-shrink: 0;
}

.back-icon.rtl {
  transform: rotate(180deg);
}

.back-label {
  flex: 1;
  text-align: left;
}

.back-button.rtl .back-label {
  text-align: right;
}

/* =============== MOBILE SIDEBAR =============== */
.mobile-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 40;
  animation: fadeIn 0.3s ease-out;
}

@media (min-width: 1024px) {
  .mobile-overlay {
    display: none;
  }
}

.mobile-sidebar {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  width: 280px;
  background-color: #ffffff;
  border-right: 1px solid #e5e7eb;
  z-index: 50;
  transform: translateX(-100%);
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.mobile-sidebar.rtl {
  left: auto;
  right: 0;
  border-right: none;
  border-left: 1px solid #e5e7eb;
  transform: translateX(100%);
}

.mobile-sidebar.open {
  transform: translateX(0);
}

@media (min-width: 1024px) {
  .mobile-sidebar {
    display: none;
  }
}

.mobile-sidebar-header {
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  border-bottom: 1px solid #e5e7eb;
}

.mobile-sidebar-header.rtl {
  flex-direction: row-reverse;
}

.mobile-close-btn {
  padding: 8px;
  border-radius: 8px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-close-btn:hover {
  background-color: #F3F4F6;
}

/* =============== MAIN CONTENT =============== */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.main-header {
  height: 72px;
  background-color: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.main-header.rtl {
  flex-direction: row-reverse;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-left.rtl {
  flex-direction: row-reverse;
}

.mobile-menu-btn {
  padding: 8px;
  border-radius: 8px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-menu-btn:hover {
  background-color: #F3F4F6;
}

@media (min-width: 1024px) {
  .mobile-menu-btn {
    display: none;
  }
}

.page-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: -0.3px;
}

.page-title.rtl {
  text-align: right;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-actions.rtl {
  flex-direction: row-reverse;
}

.header-action-btn {
  position: relative;
  padding: 10px;
  border-radius: 8px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-action-btn:hover {
  background-color: #F3F4F6;
  transform: scale(1.05);
}

.notification-btn {
  position: relative;
}

.notification-dot {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  background-color: #EF4444;
  border-radius: 50%;
  border: 2px solid #ffffff;
}

.header-actions.rtl .notification-dot {
  right: auto;
  left: 8px;
}

.mobile-hidden {
  display: flex;
}

@media (max-width: 768px) {
  .mobile-hidden {
    display: none;
  }
}

.mobile-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.2));
  display: flex;
  justify-content: center;
  align-items: center;
  display: flex;
}

@media (min-width: 1024px) {
  .mobile-avatar {
    display: none;
  }
}

.mobile-avatar-text {
  font-size: 16px;
  font-weight: 700;
  color: #10B981;
}

.page-content {
  flex: 1;
  overflow-y: auto;
  background-color: #F9FAFB;
}

/* =============== SCROLLBAR =============== */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #F3F4F6;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* =============== ANIMATIONS =============== */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* =============== RESPONSIVE =============== */
@media (max-width: 768px) {
  .main-header {
    padding: 0 16px;
  }

  .page-title {
    font-size: 18px;
  }

  .contractor-info-card {
    padding: 16px;
  }

  .sidebar-nav {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .mobile-sidebar {
    width: 85vw;
    max-width: 280px;
  }

  .page-title {
    font-size: 16px;
  }

  .header-actions {
    gap: 8px;
  }

  .header-action-btn {
    padding: 8px;
  }
}

/* =============== ACCESSIBILITY =============== */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

.nav-item:focus-visible,
.back-button:focus-visible,
.header-action-btn:focus-visible,
.sidebar-toggle-btn:focus-visible {
  outline: 3px solid #0ea5e9;
  outline-offset: 2px;
}
`;
